/**
 * 表示用タイムゾーン。
 *
 * 開発メモ・docs では時刻は一貫して UTC で扱ってきたが（複数機の時刻同期の
 * 基準として絶対時刻が要るため）、画面表示は現地時刻のほうが読みやすい。
 * 世界中のタイムゾーンを選べるようにする必要はなく、実際に使う場所
 * （日本／9月からはポーランド）の2つだけで足りる。
 *
 * 内部のデータ（FixSample.t 等）は今までどおり UTC epoch ミリ秒のまま。
 * 変換は表示直前にここで行う。
 */

export type TimeZoneId = 'Asia/Tokyo' | 'Europe/Warsaw';

export const TIME_ZONES: Array<{ id: TimeZoneId; label: string }> = [
  { id: 'Asia/Tokyo', label: '日本 (JST)' },
  { id: 'Europe/Warsaw', label: 'ポーランド (CET/CEST)' },
];

export const DEFAULT_TIME_ZONE: TimeZoneId = 'Asia/Tokyo';

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * 指定タイムゾーンでの壁時計表記を取り出す。
 *
 * `Intl.DateTimeFormat` の `timeZoneName` は ICU のバージョンでブラウザごとに
 * 表記が揺れる（"JST" だったり "GMT+9" だったりする）ため、時差を自前で
 * 計算して JST / CET / CEST を確定的に出す。やり方は定番のトリック:
 * 対象タイムゾーンでの壁時計の値を「UTCの値だったとして」epoch に直し、
 * 本来の epoch との差が時差になる。
 */
function wallClockIn(epochMs: number, tz: TimeZoneId): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(epochMs);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // hour12:false でも真夜中は "24" を返す実装があるため 24 を 0 に丸める
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

function zoneAbbreviation(tz: TimeZoneId, offsetMin: number): string {
  if (tz === 'Asia/Tokyo') return 'JST'; // 日本は夏時間が無いので常に固定
  return offsetMin >= 120 ? 'CEST' : 'CET'; // ポーランドは夏時間で UTC+1 / +2 が切り替わる
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export interface ZonedTime {
  /** HH:MM:SS */
  time: string;
  /** YYYY-MM-DD HH:MM */
  dateTime: string;
  /** JST / CET / CEST */
  zone: string;
}

/** UTC epoch ミリ秒を、指定タイムゾーンの表示用に変換する */
export function toZonedTime(epochMs: number, tz: TimeZoneId): ZonedTime {
  const w = wallClockIn(epochMs, tz);
  // 対象タイムゾーンの壁時計を「UTCの値」とみなして epoch に直し、本来の
  // epoch との差から時差を逆算する
  const asIfUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const offsetMin = Math.round((asIfUtc - epochMs) / 60000);
  return {
    time: `${pad2(w.hour)}:${pad2(w.minute)}:${pad2(w.second)}`,
    dateTime: `${w.year}-${pad2(w.month)}-${pad2(w.day)} ${pad2(w.hour)}:${pad2(w.minute)}`,
    zone: zoneAbbreviation(tz, offsetMin),
  };
}
